import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProjectProgress } from '../types/pm';
import { isPastDue } from '../utils/dateUtils';

/** 由任務、里程碑算進度（規則同後端列表 API：有任務看任務，沒有任務看里程碑） */
export const computeProgress = (
  tasks: { status: string; dueDate?: string | null }[],
  milestones: { status: string }[]
): ProjectProgress => {
  const source = tasks.length > 0 ? tasks : milestones;
  return {
    basis: tasks.length > 0 ? 'tasks' : milestones.length > 0 ? 'milestones' : null,
    total: source.length,
    completed: source.filter(item => item.status === 'COMPLETED').length,
    overdueTasks: tasks.filter(task => task.status !== 'COMPLETED' && isPastDue(task.dueDate)).length,
  };
};

/**
 * 專案進度：百分比＋細長條（主題色）＋逾期任務數（紅字，有才顯示）
 * 沒有任務也沒有里程碑時顯示「—」
 */
const ProjectProgressBar: React.FC<{ progress?: ProjectProgress }> = ({ progress }) => {
  const { t } = useTranslation(['translation', 'pages']);
  if (!progress || !progress.basis) return <span className="text-fg-muted">—</span>;

  const percent = progress.total ? Math.round((progress.completed / progress.total) * 100) : 0;
  const unit = t(progress.basis === 'tasks' ? 'pmProjects.progress.tasks' : 'pmProjects.progress.milestones', { ns: 'pages' });
  return (
    <div className="min-w-0" title={`${progress.completed} / ${progress.total} ${unit}`}>
      <div className="flex items-baseline justify-between gap-2 text-[13px] tabular-nums">
        <span className="text-fg">{percent}%</span>
        {progress.overdueTasks > 0 && (
          <span className="text-red-600 dark:text-red-400">{t('pmProjects.progress.overdue', { ns: 'pages', count: progress.overdueTasks })}</span>
        )}
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-accent-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

export default ProjectProgressBar;
